import { Fragment } from "react"
import { toast, Slide } from 'react-toastify'
import Avatar from '@components/avatar'
import { Coffee, X } from 'react-feather'
import { FormattedMessage } from 'react-intl'

export const MFA_CODE_INVALID = 4046
export const MFA_CHALLENGE_EXPIRED = 4047
export const MFA_BLOCKED = 4048
export const MFA_THROTTLED = 4049
export const MFA_REQUIRED = 4050
export const MFA_ALREADY_ENABLED = 4051
export const MFA_SESSION_ALREADY_VERIFIED = 4052

export const MFA_PENDING_STATUS_KEY = 'mfaPendingStatus'
export const MFA_PENDING_PATH = '/mfa-pending'

export const ToastContent = ({ titulo, msg, icon }) => (
  <Fragment>
    <div className='toastify-header'>
      <div className='title-wrapper'>
        {(icon === "success") && (<Avatar size='sm' color='success' icon={<Coffee size={12} />} />)}
        {(icon === "error") && (<Avatar size='sm' color='danger' icon={<X size={12} />} />)}

        <h6 className='toast-title font-weight-bold'>{titulo}</h6>
      </div>
    </div>
    <div className='toastify-body'>
      <span>{msg}</span>
    </div>
  </Fragment>
)

export const showResultToast = (intl, result) => {
  if (!result || result.silent || !result.statusMsg) return
  if (result.return) {
    toast.success(
      <ToastContent titulo={intl.formatMessage({ id: "login.success" })} msg={result.statusMsg} icon={'success'} />,
      { transition: Slide, hideProgressBar: true, autoClose: 2000 }
    )
  } else {
    toast.error(
      <ToastContent titulo={intl.formatMessage({ id: "login.error" })} msg={result.statusMsg} icon={'error'} />,
      { transition: Slide, hideProgressBar: true, autoClose: 5000 }
    )
  }
}

// Apollo 3 puts errors of an HTTP 4xx/5xx response in networkError.result.errors, not graphQLErrors.
export const getGqlError = (e) => {
  if (!e) return null
  if (e.graphQLErrors && e.graphQLErrors.length > 0) return e.graphQLErrors[0]
  const result = e.networkError && e.networkError.result
  if (result && Array.isArray(result.errors) && result.errors.length > 0) return result.errors[0]
  return null
}

export const getGqlExtensions = (e) => {
  const gqlError = getGqlError(e)
  return (gqlError && gqlError.extensions) || {}
}

// Backend message for a wrong email or password (same message for both).
const INVALID_CREDENTIALS_PATTERN = /contrase/i
export const isInvalidCredentialsError = (e) => {
  const gqlError = getGqlError(e)
  const extensions = getGqlExtensions(e)
  return extensions.code === 'BAD_USER_INPUT' && !!gqlError && INVALID_CREDENTIALS_PATTERN.test(gqlError.message || '')
}

const UNAVAILABLE_CODES = ['MFA_CHECK_UNAVAILABLE', 'SESSION_CHECK_UNAVAILABLE', 'SERVICE_UNAVAILABLE']

// Logs only the code and HTTP status, never the body (it may carry user data).
const logUnexpectedError = (e, code) => {
  const status = e && e.networkError && e.networkError.statusCode
  console.warn('login: error no reconocido', { code: code || null, status: status || null })
}

export const buildNonMfaError = (e, intl, fallbackId = "login.mfa.genericError") => {
  const extensions = getGqlExtensions(e)
  if (extensions.code === 'BAD_USER_INPUT') {
    const id = isInvalidCredentialsError(e) ? "login.errorSignIn" : "login.mfa.invalidRequest"
    return { return: false, statusMsg: intl.formatMessage({ id }) }
  }
  if (UNAVAILABLE_CODES.includes(extensions.code)) {
    return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.serviceUnavailable" }) }
  }
  if (!extensions.code && e && e.networkError) {
    const status = e.networkError.statusCode
    const id = status && status >= 500 ? "login.mfa.serviceUnavailable" : "login.mfa.networkError"
    return { return: false, statusMsg: intl.formatMessage({ id }) }
  }
  logUnexpectedError(e, extensions.code)
  return { return: false, statusMsg: intl.formatMessage({ id: fallbackId }) }
}

// Guards against bouncing between / and /mfa-pending: stop after too many redirects in a short time.
const REDIRECT_GUARD_KEY = 'mfaPendingRedirects'
const REDIRECT_GUARD_WINDOW_MS = 2 * 60 * 1000
const REDIRECT_GUARD_MAX = 3

export const allowMfaRedirect = () => {
  const now = Date.now()
  let recent = []
  try {
    recent = JSON.parse(sessionStorage.getItem(REDIRECT_GUARD_KEY)) || []
  } catch (err) {
    recent = []
  }
  recent = recent.filter(ts => now - ts < REDIRECT_GUARD_WINDOW_MS)
  if (recent.length >= REDIRECT_GUARD_MAX) {
    console.warn('login: se detuvo un rebote de redirecciones del MFA pendiente')
    return false
  }
  recent.push(now)
  try {
    sessionStorage.setItem(REDIRECT_GUARD_KEY, JSON.stringify(recent))
  } catch (err) {
    // Without sessionStorage there is no guard, but navigation still works.
  }
  return true
}

export const resetMfaRedirectGuard = () => {
  try {
    sessionStorage.removeItem(REDIRECT_GUARD_KEY)
  } catch (err) {
    // Nothing to clear.
  }
}

export const LOGIN_NOTICE_KEY = 'loginNotice'
const LOGIN_NOTICE_IDS = {
  SESSION_EXPIRED: "login.sessionExpired",
  SESSION_REVOKED: "login.sessionRevoked"
}

export const setLoginNotice = (reason) => {
  try {
    sessionStorage.setItem(LOGIN_NOTICE_KEY, reason)
  } catch (err) {
    // The notice is optional.
  }
}

export const takeLoginNotice = (intl) => {
  let reason = null
  try {
    reason = sessionStorage.getItem(LOGIN_NOTICE_KEY)
    sessionStorage.removeItem(LOGIN_NOTICE_KEY)
  } catch (err) {
    reason = null
  }
  const id = reason && LOGIN_NOTICE_IDS[reason]
  return id ? { return: false, statusMsg: intl.formatMessage({ id }) } : null
}

export const removeCurrentAccountFromList = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData'))
    const userId = userData && userData.user && userData.user._id
    const accounts = JSON.parse(localStorage.getItem('listAcc')) || []
    const usertoken = localStorage.getItem('usertoken')
    const remaining = accounts.filter(acc => {
      const accUserId = acc && acc.userData && acc.userData.user && acc.userData.user._id
      return !((userId && accUserId === userId) || (usertoken && acc && acc.usertoken === usertoken))
    })
    localStorage.setItem('listAcc', JSON.stringify(remaining))
  } catch (err) {
    // Corrupt listAcc: leave it untouched.
  }
}

export const AccountBadge = ({ email }) => {
  const instance = localStorage.getItem('playground')
  if (!email && !instance) return null
  return (
    <div className='text-center mb-2'>
      {email && (<div><small><FormattedMessage id="login.mfa.account" />: <strong>{email}</strong></small></div>)}
      {instance && (<div><small><FormattedMessage id="login.mfa.instance" />: <strong>{instance}</strong></small></div>)}
    </div>
  )
}

export const buildMfaError = (e, intl, resetToStart) => {
  const extensions = getGqlExtensions(e)
  const description = extensions.description || {}
  if (extensions.code !== 'MFA_ERROR') {
    return buildNonMfaError(e, intl)
  }
  switch (extensions.mfaCode) {
    case MFA_CODE_INVALID:
      return {
        return: false,
        statusMsg: typeof description.attemptsLeft === 'number'
          ? intl.formatMessage({ id: "login.mfa.invalidCode" }, { n: description.attemptsLeft })
          : intl.formatMessage({ id: "login.mfa.invalidCodeNoAttempts" })
      }
    case MFA_CHALLENGE_EXPIRED:
      resetToStart()
      return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.challengeExpired" }) }
    case MFA_BLOCKED: {
      const minutes = description.blockedUntil
        // 30 s tolerance for clock skew so a fresh 10 min block does not read 11.
        ? Math.max(1, Math.ceil((new Date(description.blockedUntil).getTime() - Date.now() - 30000) / 60000))
        : 10
      resetToStart()
      return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.blocked" }, { m: minutes }) }
    }
    case MFA_REQUIRED:
      resetToStart()
      return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.mfaRequired" }) }
    case MFA_ALREADY_ENABLED:
      // Enrolled elsewhere (another device or instance): start again with the existing authenticator.
      resetToStart()
      return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.alreadyEnabled" }) }
    case MFA_THROTTLED:
      return {
        return: false,
        retryInSeconds: description.retryInSeconds,
        statusMsg: intl.formatMessage({ id: "login.mfa.throttled" }, { s: description.retryInSeconds || 60 })
      }
    default:
      return { return: false, statusMsg: intl.formatMessage({ id: "login.mfa.genericError" }) }
  }
}
